import React from 'react'

/**
 * Tone vocabulary for badges across the dashboard.
 *
 * - `neutral` (default): muted accent, used for descriptive metadata
 * - `ok`: positive signals (verified, ready, resolved-yes)
 * - `warn`: stale, partial, or attention-needed states
 * - `bad`: errors, overdue, resolved-no
 * - `accent`: emphasises selected / primary state without strong polarity
 *
 * This wraps the existing CSS `.pill` / `.pill ok` / etc. classes so the
 * tone vocabulary lives in TypeScript rather than scattered class strings.
 */
export type PillTone = 'neutral' | 'ok' | 'warn' | 'bad' | 'accent'

export type PillProps = {
  tone?: PillTone
  /** Optional native `title` tooltip text. */
  title?: string
  className?: string
  style?: React.CSSProperties
  children: React.ReactNode
  /** Used for screen-reader-only context when the visible text is terse. */
  ariaLabel?: string
}

const TONE_TO_CLASS: Record<PillTone, string> = {
  neutral: 'pill',
  ok: 'pill ok',
  warn: 'pill warn',
  bad: 'pill bad',
  accent: 'pill',
}

const TONE_TO_STYLE: Partial<Record<PillTone, React.CSSProperties>> = {
  accent: { background: 'rgba(99,102,241,0.12)', borderColor: 'rgba(99,102,241,0.35)', color: 'var(--accent, #6366f1)' },
}

export default function Pill({ tone = 'neutral', title, className, style, ariaLabel, children }: PillProps) {
  const cls = [TONE_TO_CLASS[tone], className].filter(Boolean).join(' ')
  const inlineStyle = { ...(TONE_TO_STYLE[tone] || {}), ...(style || {}) }
  return (
    <span className={cls} title={title} aria-label={ariaLabel} style={inlineStyle}>
      {children}
    </span>
  )
}
